import { ChevronDown } from 'lucide-react';

import { Button } from '@/_shared/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/_shared/components/ui/collapsible';

interface Tool {
  name: string;
  description: string;
}

// Keep in sync with src/mcp-external/tools/
const TOOL_GROUPS: { label: string; tools: Tool[] }[] = [
  {
    label: 'Warehouse',
    tools: [
      { name: 'query_warehouse', description: 'Execute read-only SQL queries' },
      { name: 'list_tables', description: 'List tables in a warehouse' },
      { name: 'describe_table', description: 'Describe columns and types of a table' },
      { name: 'list_warehouses', description: 'Discover available warehouses' },
    ],
  },
  {
    label: 'Dashboard',
    tools: [
      { name: 'list_dashboards', description: 'List workspace dashboards' },
      { name: 'get_dashboard', description: 'View a dashboard with live data' },
      { name: 'query_dashboard_panel', description: 'Query a specific dashboard panel' },
      { name: 'save_dashboard', description: 'Create or update dashboards' },
    ],
  },
  {
    label: 'Context Hub',
    tools: [
      { name: 'list_context_items', description: 'List notes, memories, skills and generated docs' },
      { name: 'search_context', description: 'Search the Context Hub' },
      { name: 'get_context_item', description: 'Read one item in full' },
      {
        name: 'create_context_item',
        description: 'Create a note, memory or skill (owners and admins with Context Hub access)',
      },
      {
        name: 'update_context_item',
        description: 'Edit a note, memory or skill (owners and admins with Context Hub access)',
      },
      {
        name: 'delete_context_item',
        description: 'Delete a note, memory or skill (owners and admins with Context Hub access)',
      },
      {
        name: 'regenerate_context_doc',
        description: 'Regenerate a generated doc (owners and admins with Context Hub access)',
      },
      { name: 'delete_context_doc', description: 'Delete a generated doc (owners and admins with Context Hub access)' },
    ],
  },
];

function ToolGroup({ label, tools }: { label: string; tools: Tool[] }): React.ReactElement {
  return (
    <div>
      <p className="mb-1.5 text-xs font-medium text-muted-foreground">{label}</p>
      <dl className="space-y-0.5">
        {tools.map((tool) => (
          <div key={tool.name} className="flex items-baseline gap-2">
            <dt className="shrink-0">
              <code className="rounded bg-card px-1.5 py-0.5 font-mono text-xs text-foreground ring-1 ring-border ring-inset">
                {tool.name}
              </code>
            </dt>
            <dd className="text-xs text-muted-foreground">{tool.description}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function McpAvailableTools(): React.ReactElement {
  return (
    <div className="border-t border-border px-4 py-3">
      <Collapsible className="grid gap-3">
        <CollapsibleTrigger asChild>
          <Button
            type="button"
            variant="text"
            className="group w-full justify-between gap-3 text-left whitespace-normal hover:text-foreground"
            suffixIcon={
              <ChevronDown className="shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
            }
          >
            <span className="block min-w-0">
              <span className="block text-sm font-medium text-foreground">Available tools</span>
              <span className="mt-0.5 block text-xs font-normal text-muted-foreground">
                Connected clients can use these tools to interact with your workspace.
              </span>
            </span>
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="space-y-4">
            {TOOL_GROUPS.map((group) => (
              <ToolGroup key={group.label} label={group.label} tools={group.tools} />
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
